/* eslint-disable no-await-in-loop, no-console */
import debugModule from 'debug'
import Config from '@/config'

debugModule.useColors = () => false
debugModule.disable()
const logger = debugModule('aha')

const logfileNameStorageKey = 'log-filename'
const maxLogFileCount = 10
const maxLogFileSize = 5 * 1024 * 1024 // 2MB
const bufferLengthBeforeFlush = 20
const logToConsole = ['staging', 'develop', 'local'].includes(Config.env)

let logHandle = null
const logBuffer = []

const getLogFilePrefix = (filename) => {
  // Use regex to match and remove optional rotation number and .log extension
  // Pattern: .{number}.log (where .{number} is optional) at the end of filename
  const match = filename.match(/^(.+?)(?:\.(\d+))?\.log$/)
  if (match) {
    // Return the prefix without optional rotation number and .log extension
    return match[1]
  }

  // Fallback: remove .log extension if pattern doesn't match
  return filename.replace(/\.log$/, '')
}

const getLogDirectory = async () => {
  const opfsRoot = await navigator.storage.getDirectory()
  return opfsRoot.getDirectoryHandle('aha-logs', { create: true })
}

const getSessionLogFile = () => sessionStorage.getItem(logfileNameStorageKey)
const setSessionLogFile = (filename) => sessionStorage.setItem(logfileNameStorageKey, filename)

const rotateLogFile = async (logfileName) => {
  // Find the next available rotation number for the new log file
  let rotationNumber = 1
  const baseFilename = getLogFilePrefix(logfileName)
  let fileExists = true

  while (fileExists) {
    const newFilename = `${baseFilename}.${rotationNumber}.log`
    try {
      const logsDir = await getLogDirectory()
      await logsDir.getFileHandle(newFilename)
      rotationNumber++
    } catch (error) {
      // File doesn't exist, use this filename for the new log file
      fileExists = false
    }
  }

  // Generate new filename with rotation number
  const newFilename = `${baseFilename}.${rotationNumber}.log`

  // Update session storage to use the new filename for future logging
  setSessionLogFile(newFilename)

  // Return the new filename so the caller can get a fresh handle
  return newFilename
}

// Generate unique log file name for browser tab
const getLogFileName = () => {
  // Check if filename already exists in sessionStorage
  const existingFilename = getSessionLogFile()
  if (existingFilename) {
    return existingFilename
  }

  // Generate new unique filename
  const timestamp = new Date().toISOString().replace(/[:.]/g, '-')
  const tabId = Math.random().toString(36).substring(2, 9)
  const filename = `app-${timestamp}-${tabId}.log`

  // Store in sessionStorage for this tab
  setSessionLogFile(filename)

  return filename
}

// Helper function to check if a file exists
const checkFileExists = async (logsDir, filename) => {
  try {
    await logsDir.getFileHandle(filename)
    return true
  } catch (error) {
    return false
  }
}

// Helper function to collect log files for cleanup
const collectLogFiles = async (logsDir, lockFileName) => {
  // Convert async iterator to array
  const entries = []
  const iterator = logsDir.entries()
  let result = await iterator.next()

  while (!result.done) {
    entries.push(result.value)
    result = await iterator.next()
  }

  // Process entries using array methods
  const filePromises = entries
    .filter(([name, handle]) => handle.kind === 'file' && name !== lockFileName)
    .map(async ([name, handle]) => {
      const file = await handle.getFile()
      return {
        name,
        handle,
        lastModified: file.lastModified,
      }
    })

  return Promise.all(filePromises)
}

// Helper function to perform cleanup with lock
const performCleanupWithLock = async (logsDir) => {
  const lockFileName = '.cleanup.lock'
  let lockHandle = null
  let lockWriter = null

  try {
    // Try to acquire exclusive lock
    lockHandle = await logsDir.getFileHandle(lockFileName, { create: true })
    lockWriter = await lockHandle.createWritable({ keepExistingData: false, exclusive: true })
    await lockWriter.write(Date.now().toString())

    // Perform cleanup while holding the lock
    const files = await collectLogFiles(logsDir, lockFileName)

    if (files.length >= maxLogFileCount) {
      // Sort by lastModified (oldest first) and remove the oldest file
      files.sort((a, b) => a.lastModified - b.lastModified)
      await logsDir.removeEntry(files[0].name)
    }
  } catch (error) {
    console.debug('Cleanup already in progress by another tab')
  } finally {
    // Always release the lock
    if (lockWriter) {
      try {
        await lockWriter.close()
        await logsDir.removeEntry(lockFileName)
      } catch (error) {
        console.debug('Failed to release cleanup lock:', error)
      }
    }
  }
}

const getLogFileHandle = async (filename = getLogFileName()) => {
  // Check if OPFS is supported
  if (!navigator.storage || !navigator.storage.getDirectory) {
    throw new Error('OPFS not supported in this browser')
  }

  const logsDir = await getLogDirectory()
  const fileExists = await checkFileExists(logsDir, filename)

  // If file doesn't exist, perform cleanup before creating
  if (!fileExists) {
    await performCleanupWithLock(logsDir)
  }

  // Create or get the log file
  return logsDir.getFileHandle(filename, { create: true })
}

// Set of keys to ignore during logging
const ignoredKeys = new Set([
  'xlarge',
  'xxlarge',
  'large',
  'thumbnail',
  'backgroundimage',
  'colorpalette',
])

// Helper function to check if a value should be ignored
const shouldIgnoreValue = (key, value, ignoreNullValue) => {
  if (typeof value === 'function') return true
  if (ignoreNullValue && (value === null || value === undefined)) return true
  if (key.startsWith('_') || key.startsWith('$')) return true
  if (typeof value === 'string' && value.length > 300) return true
  if (ignoredKeys.has(key.toLowerCase())) return true
  return false
}

// Helper function to check if an object is a Vue component
const isVueComponent = (value) => {
  return value._isVue || value.$options || value.$el ||
    (value.constructor && (value.constructor.name === 'VueComponent' || value.constructor.name === 'Vue'))
}

function jsonStringifyWithDepth(obj, maxDepth = 1, ignoreNullValue = true) {
  const seen = new WeakSet()

  return JSON.stringify(obj, (key, value) => {
    if (shouldIgnoreValue(key, value, ignoreNullValue)) {
      return undefined
    }

    if (typeof value === 'object' && value !== null) {
      if (isVueComponent(value) || seen.has(value)) {
        return undefined
      }

      seen.add(value)

      const depth = key.split('.').length
      if (depth > maxDepth) {
        return undefined
      }
    }
    return value
  })
}

const getFileSize = async (fileHandle) => {
  const file = await fileHandle.getFile()
  return file.size
}

const flushLog = async () => {
  if (logBuffer.length === 0) return

  // Check if log writer is initialized
  if (!logHandle) {
    logHandle = await getLogFileHandle()
  }

  // Check file size before writing (5MB = 5 * 1024 * 1024 bytes)
  const currentSize = await getFileSize(logHandle)

  if (currentSize >= maxLogFileSize) {
    // Rotate the log file (this updates the filename in session storage)
    const currentFilename = getLogFileName()
    const newFilename = await rotateLogFile(currentFilename)

    // Get a fresh handle to the new log file
    logHandle = await getLogFileHandle(newFilename)
  }

  const writable = await logHandle.createWritable({ keepExistingData: true, exclusive: true })
  await writable.seek(await getFileSize(logHandle))

  // Write all lines using Promise.all instead of for loop
  await Promise.all(logBuffer.map((line) => writable.write(line)))

  await writable.close()
  logBuffer.length = 0
}

// Write message to log file
const writeLog = async (...args) => {
  try {
    // Format arguments like console.log does
    const message = args.map((arg) => {
      if (typeof arg === 'object') {
        try {
          return jsonStringifyWithDepth(arg)
        } catch {
          return '[object]'
        }
      }
      return String(arg)
    }).join(' ')

    // Format message without timestamp
    const logEntry = `${new Date().toISOString()} ${message}\n`

    logBuffer.push(logEntry)
    if (logBuffer.length > bufferLengthBeforeFlush) {
      await flushLog()
    }

    return true
  } catch (error) {
    console.error('Failed to write to log file:', error)
    return false
  }
}

const onPageHide = () => {
  flushLog().catch((error) => {
    console.error('Failed to flush log on page hide:', error)
  })
}

// Setup cleanup event listeners
const setupCleanup = () => {
  // Handle tab/window close using pagehide event
  window.addEventListener('pagehide', onPageHide)
}

// Assign writeLog to debug.log for compatibility
logger.log = logToConsole ? (...args) => {
  const hasError = args.some((arg) => arg instanceof Error)
  if (hasError) {
    console.error(...args)
  } else {
    console.log(...args)
  }
  writeLog(...args)
} : (...args) => writeLog(...args)

const enableLog = () => {
  debugModule.enable('aha*')
  // Initialize cleanup on module load
  setupCleanup()
}

// Scan all log files in OPFS and return their metadata
const scanLogFiles = async () => {
  try {
    const logsDir = await getLogDirectory()

    // Convert async iterator to array
    const entries = []
    const iterator = logsDir.entries()
    let result = await iterator.next()

    while (!result.done) {
      entries.push(result.value)
      result = await iterator.next()
    }

    // Process entries using array methods
    const logFilePromises = entries
      .filter(([name, handle]) => handle.kind === 'file' && name.endsWith('.log') && name !== '.cleanup.lock')
      .map(async ([name, handle]) => {
        const file = await handle.getFile()
        return {
          name,
          handle,
          size: file.size,
          lastModified: file.lastModified,
          file,
        }
      })

    let logFiles = await Promise.all(logFilePromises)
    // Sort by lastModified (newest first)
    logFiles = logFiles.filter((logFile) => logFile.size > 0).sort((a, b) => b.lastModified - a.lastModified)
    return logFiles
  } catch (error) {
    console.error('Failed to scan log files:', error)
    return []
  }
}

// Read log file content as text
const readLogFile = async (fileHandle) => {
  try {
    const file = await fileHandle.getFile()
    return await file.text()
  } catch (error) {
    console.error('Failed to read log file:', error)
    return null
  }
}

const disableLog = () => {
  debugModule.disable()
  window.removeEventListener('pagehide', onPageHide)
}

export {
  enableLog,
  disableLog,
  getLogFilePrefix,
  rotateLogFile,
  writeLog,
  bufferLengthBeforeFlush,
  scanLogFiles,
  readLogFile,
  flushLog,
}
export default logger
